import axios, { type AxiosRequestConfig, isAxiosError } from 'axios'
import axiosRetry from 'axios-retry'
import type { Logger } from '@vp/ubik-logging'

import { REQUESTOR } from '../utils/constants'

interface CartClientRequestConfig extends AxiosRequestConfig {
  logger?: Logger
  context?: Record<string, unknown> // Additional context for logging
  metadata?: {
    startTime: number
  }
}

const baseUrl = 'https://cart.large-assortment.vpsvc.com/v1'

// Default timeout of 10 seconds for cart requests
const DEFAULT_TIMEOUT_MS = 10000

const axiosInstance = axios.create({
  timeout: DEFAULT_TIMEOUT_MS,
})

// Track request start time
axiosInstance.interceptors.request.use((config) => {
  const customConfig = config as CartClientRequestConfig
  customConfig.metadata = { startTime: Date.now() }
  return config
})

// Log requests with timing
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error.config as CartClientRequestConfig
    const duration = config?.metadata?.startTime ? Date.now() - config.metadata.startTime : null
    const logger = config?.logger
    const context = config?.context || {}
    const status = error.response?.status

    if (logger) {
      logger.warn('Cart request failed in interceptor', {
        durationMs: duration,
        url: config?.url,
        method: config?.method?.toUpperCase(),
        errorCode: error.code,
        isTimeout: error.code === 'ECONNABORTED',
        isNetworkError: !error.response,
        hasResponse: !!error.response,
        responseStatus: status,
        responseData: error.response?.data,
        errorMessage: error.message,
        ...context,
      })
    }
    return Promise.reject(error)
  },
)

axiosRetry(axiosInstance, {
  retries: 1,
  retryDelay: (retryCount) => {
    return Math.min(2000 * Math.pow(2, retryCount - 1), 5000)
  },
  retryCondition: (error) => {
    const status = error.response?.status
    const config = error.config as CartClientRequestConfig
    const logger = config?.logger
    const context = config?.context || {}

    // Log non-retriable decisions
    if (logger && (status === 400 || status === 404)) {
      logger.info('Skipping retry for non-retriable status code', { status, ...context })
    }

    // Don't retry on 400 or 404 errors
    if (status === 400 || status === 404) {
      return false
    }
    // Retry on network errors or 5xx server errors
    return axiosRetry.isNetworkOrIdempotentRequestError(error) || (status ? status >= 500 : false)
  },
  onRetry: (retryCount, error) => {
    const config = error.config as CartClientRequestConfig
    const logger = config?.logger
    const context = config?.context || {}
    const status = error.response?.status

    if (logger) {
      logger.warn('Retrying request due to error', {
        retryCount,
        status,
        errorCode: error.code,
        errorMessage: error.message,
        isNetworkError: !error.response,
        isTimeout: error.code === 'ECONNABORTED',
        ...context,
      })
    }
  },
})

export const upsertCart = async (cartTenant: string, workId: string, culture: string, authorizationHeader: string, logger: Logger): Promise<void> => {
  const url = `${baseUrl}/upsert/${workId}`

  try {
    await axiosInstance.post(url, undefined, {
      params: {
        locale: culture,
        tenant: cartTenant,
        requestor: REQUESTOR,
      },
      headers: {
        Authorization: authorizationHeader,
      },
      logger,
      context: { workId, culture },
    } as CartClientRequestConfig)
  } catch (error) {
    if (isAxiosError(error)) {
      logger.error('Upserting customer cart failed', error, {
        workId,
        culture,
        url,
        errorCode: error.code,
        isNetworkError: !error.response,
        isTimeout: error.code === 'ECONNABORTED',
        responseStatus: error.response?.status,
        responseData: error.response?.data,
      })
    } else {
      logger.error('Upserting customer cart failed', error as Error, { workId, culture, url })
    }
    throw error
  }
}

export const upsertCartMultiWork = async (
  cartTenant: string,
  workIds: string[],
  culture: string,
  authorizationHeader: string,
  logger: Logger,
): Promise<void> => {
  const url = `${baseUrl}/${cartTenant}/upsert/work`

  try {
    await axiosInstance.post(url, undefined, {
      params: {
        workIds: workIds.join(','),
        locale: culture,
        requestor: REQUESTOR,
      },
      headers: {
        Authorization: authorizationHeader,
      },
      logger,
      context: { workIds: workIds.join(','), culture },
    } as CartClientRequestConfig)
  } catch (error) {
    if (isAxiosError(error)) {
      logger.error('Upserting customer cart (multi-work) failed', error, {
        workIds: workIds.join(','),
        culture,
        url,
        errorCode: error.code,
        isNetworkError: !error.response,
        isTimeout: error.code === 'ECONNABORTED',
        responseStatus: error.response?.status,
        responseData: error.response?.data,
      })
    } else {
      logger.error('Upserting customer cart (multi-work) failed', error as Error, { workIds: workIds.join(','), culture, url })
    }
    throw error
  }
}
